import React from "react";

const HEAD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const PHASE_LEGEND = [
  ["#B5654A", "Menstrual"],
  ["#9DB5A0", "Follicular"],
  ["#D9B44A", "Ovulatory"],
  ["#A9A5CC", "Luteal"],
  ["repeating-linear-gradient(90deg,#A9A5CC 0 4px,transparent 4px 7px)", "Estimated · cycle ongoing"],
];

const NavButton = ({ label, glyph, disabled, onClick }) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="w-[30px] h-[30px] rounded-[15px] bg-[#F4F0EC] text-[15px] text-[#3a3a3a] cursor-pointer disabled:cursor-default"
    style={{ opacity: disabled ? 0.35 : 1 }}
  >
    {glyph}
  </button>
);

/**
 * Monday-first month grid with a value ramp per day ("Wellness Calendar
 * Simple" in the design). Each day carries a phase lane, on every tab.
 *
 * @param {Object} props
 * @param {Object} props.cal - From core/model/calendar.calendar.
 * @param {() => void} props.onPrev
 * @param {() => void} props.onNext
 * @param {(iso: string) => void} props.onSelect
 * @param {boolean} [props.showPhases] - The phase lane and its legend (the design's `showPhases`).
 * @param {[string, string]} [props.navLabels] - Accessible names of the month arrows.
 */
const WellnessCalendar = ({ cal, onPrev, onNext, onSelect, showPhases = true, navLabels = ["Previous month", "Next month"] }) => (
  <div className="flex flex-col gap-[16px] min-w-0">
    <div className="flex items-center justify-between gap-[12px] flex-wrap">
      <div className="flex flex-col gap-[2px] min-w-0">
        <span className="text-[16px] font-bold tracking-[-0.01em]">{cal.title}</span>
        <span className="text-[12px] text-[#6b6b6b]">{cal.sub}</span>
      </div>
      <div className="flex items-center gap-[6px]">
        <NavButton label={navLabels[0]} glyph="‹" disabled={!cal.canPrev} onClick={onPrev} />
        <span className="text-[14px] font-semibold min-w-[120px] text-center">{cal.month}</span>
        <NavButton label={navLabels[1]} glyph="›" disabled={!cal.canNext} onClick={onNext} />
      </div>
    </div>

    <div className="grid grid-cols-7 gap-x-[6px] gap-y-[4px]">
      {HEAD.map((h) => (
        <div key={h} className="text-[11px] font-semibold text-[#A39A93] pb-[4px] pl-[2px]">
          {h}
        </div>
      ))}
      {cal.cells.map((c, i) =>
        c.blank ? (
          <div key={`b${i}`} />
        ) : (
          <div key={c.iso} className="flex flex-col gap-[4px] min-w-0">
            <button
              type="button"
              title={c.tip}
              aria-label={c.tip || String(c.day)}
              disabled={!c.clickable}
              onClick={() => onSelect(c.iso)}
              className="h-[54px] rounded-[8px] px-[8px] py-[6px] flex flex-col justify-between items-start min-w-0 text-left"
              style={{ background: c.bg, color: c.fg, boxShadow: c.ring, cursor: c.clickable ? "pointer" : "default" }}
            >
              <span className="text-[11px] font-semibold leading-none" style={{ opacity: c.dop }}>
                {c.day}
              </span>
              <span className="text-[18px] font-bold tracking-[-0.02em] leading-none tabular-nums">{c.val}</span>
            </button>
            {showPhases && (
              <div className="relative h-[16px]">
                <div className="absolute left-0 top-0 h-[4px] rounded-[2px]" style={{ width: c.phW, background: c.phBg }} />
                <div className="absolute left-[1px] top-[6px] text-[10px] font-bold whitespace-nowrap" style={{ color: c.phFg }}>
                  {c.phLabel}
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>

    <div className="flex flex-col gap-[10px] pt-[12px] border-t border-[#F1ECE8]">
      <div className="flex flex-wrap items-center gap-x-[18px] gap-y-[8px]">
        <span className="text-[11px] font-bold text-[#8a6a58]">{cal.rampTitle}</span>
        <div className="flex gap-[2px]">
          {cal.bins.map((b) => (
            <span
              key={b.label}
              title={b.label}
              className="flex items-center justify-center px-[6px] h-[18px] rounded-[3px] text-[10px] text-[#2a1a12] tabular-nums"
              style={{ background: b.bg }}
            >
              {b.label}
            </span>
          ))}
        </div>
        {cal.legend.map((l) => (
          <span key={l.label} className="flex items-center gap-[6px] text-[11px] text-[#555]">
            <span
              className="w-[18px] h-[18px] rounded-[4px] flex items-center justify-center text-[9px] font-bold text-[#6b6b6b]"
              style={{ background: l.bg, boxShadow: l.ring }}
            >
              {l.txt}
            </span>
            {l.label}
          </span>
        ))}
      </div>
      {showPhases && (
        <div className="flex flex-wrap items-center gap-x-[18px] gap-y-[8px]">
          <span className="text-[11px] font-bold text-[#8a6a58]">PHASE</span>
          {PHASE_LEGEND.map(([bg, label]) => (
            <span key={label} className="flex items-center gap-[6px] text-[11px] text-[#555]">
              <span className="w-[20px] h-[4px] rounded-[2px]" style={{ background: bg }} />
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default WellnessCalendar;
